import React from 'react';
import { User, MapPin, Settings, Check, Eye, AlertTriangle } from 'lucide-react';

const StepIndicator = ({ currentStep, onStepClick, calculationResult, totalSteps = 3, staleWarning = false }) => {
  const allSteps = [
    { num: 1, label: 'Trip', fullLabel: 'Guest & Trip', desc: 'Guest info & route', icon: User },
    { num: 2, label: 'Prefs', fullLabel: 'Preferences', desc: 'Category & pricing', icon: Settings },
    { num: 3, label: 'Preview', fullLabel: 'Preview & Save', desc: 'Review and save', icon: Eye },
  ];
  
  // Use only the steps up to totalSteps
  const steps = allSteps.slice(0, totalSteps);

  const handleStepClick = (stepNum) => {
    // Backward navigation is always allowed. Forward navigation is allowed
    // once a calculation exists (Step 1 has been validated by then) so the
    // operator can hop back to Preview after editing pax / dates.
    if (stepNum < currentStep || calculationResult) {
      onStepClick?.(stepNum);
    }
  };

  return (
    <div className="flex items-center justify-between mb-4 sm:mb-6 bg-white rounded-lg border border-[var(--g200)] p-3 sm:p-4">
      {steps.map((s, idx) => {
        const isActive = s.num === currentStep;
        const isCompleted = s.num < currentStep;
        const isPending = s.num > currentStep;
        // Clickable: any completed step, or any step once a calculation exists
        const isClickable = s.num !== currentStep && (s.num < currentStep || !!calculationResult);
        // Step 3 is disabled if no calculation
        const isDisabled = s.num === 3 && !calculationResult;
        const Icon = s.icon;
        
        return (
          <div key={s.num} className="flex items-center flex-1">
            <div 
              className={`flex items-center gap-2 sm:gap-3 flex-1 ${isClickable ? 'cursor-pointer group' : ''} ${isDisabled ? 'opacity-50' : ''}`}
              onClick={() => isClickable && handleStepClick(s.num)}
              title={isDisabled ? 'Calculate quote first to unlock preview' : ''}
              data-testid={`step-indicator-${s.num}`}
            >
              {/* Step Bubble with Tick Overlay */}
              <div className="relative flex-shrink-0">
                <div
                  className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    isActive 
                      ? 'bg-[var(--navy)] text-white' 
                      : isCompleted 
                        ? 'bg-transparent border-[1.5px] border-[var(--g300)] text-[var(--g500)]' 
                        : 'bg-transparent border-[1.5px] border-[var(--g300)] text-[var(--g400)]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </div>
                {/* Done Tick Badge - positioned bottom-right */}
                {isCompleted && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-[13px] h-[13px] rounded-full bg-[var(--g500)] flex items-center justify-center border-2 border-white">
                    <Check className="w-2 h-2 text-white" strokeWidth={3} />
                  </div>
                )}
              </div>
              
              {/* Step Text */}
              <div className="min-w-0">
                {/* Mobile: Short label only */}
                <p className={`font-semibold text-[11px] sm:hidden ${
                  isActive ? 'text-[var(--navy)]' : isCompleted ? 'text-[var(--g600)]' : 'text-[var(--g400)]'
                }`}>
                  {s.label}
                </p>
                {/* Desktop: Full label */}
                <p className={`font-semibold text-sm hidden sm:block ${
                  isActive ? 'text-[var(--navy)]' : isCompleted ? 'text-[var(--g600)]' : 'text-[var(--g400)]'
                }`}>
                  {s.fullLabel}
                </p>
                {/* Stale-calc badge — prices on Preview no longer match the inputs */}
                {s.num === 3 && staleWarning && calculationResult && (
                  <span
                    className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 ring-1 ring-amber-300"
                    data-testid="step-3-stale-badge"
                  >
                    <AlertTriangle className="h-2.5 w-2.5" />
                    <span className="hidden sm:inline">Recalculate required</span>
                    <span className="sm:hidden">Stale</span>
                  </span>
                )}
              </div>
            </div>
            
            {/* Connector Line */}
            {idx < steps.length - 1 && (
              <div 
                className={`h-[1px] flex-1 mx-2 sm:mx-4 min-w-[24px] sm:min-w-[40px] max-w-[60px] sm:max-w-[100px] ${
                  isCompleted ? 'bg-[var(--navy)] opacity-20' : 'bg-[var(--g200)]'
                }`} 
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default StepIndicator;
